"use client";

import { useRouter, usePathname } from "next/navigation";
import { useCallback } from "react";
import { useGlobalStateContext } from "@/context/GlobalContext";
import { PANEL_LIST_MODULE_PATHS } from "@/utils/panelList/panelListNavigate";
import statusConfig from "../../../status-config.json";

export const useNavigationHelper = () => {
  const router = useRouter();
  const pathname = usePathname();

  const goToProductPage = (productId: string, params?: any) => {
    let routePath: string = PANEL_LIST_MODULE_PATHS.SHOPPING_LIST;

    if (pathname.includes("prepare-for-panel")) {
      routePath = PANEL_LIST_MODULE_PATHS.PREPARE_FOR_PANEL;
    } else if (pathname.includes("vendor-assessment")) {
      routePath = PANEL_LIST_MODULE_PATHS.VENDOR_ASSESSMENT;
    } else if (pathname.includes("business-review-panel")) {
      routePath = PANEL_LIST_MODULE_PATHS.BUSINESS_REVIEW_PANEL;
    } else if (pathname.includes("tasting-panel")) {
      routePath = PANEL_LIST_MODULE_PATHS.TASTING_PANEL;
    }
    const query = new URLSearchParams(params).toString();
    router.push(routePath + "/" + productId + "?" + query);
  };

  const getFeatureIdsFromPath = (features: any) => {
    let submenuItem;
    let menu = features.mainSidebarItems.find((menu: any) => {
      return menu.pathPrefix && pathname.includes(menu.pathPrefix);
    });
    
    if (menu) {
      submenuItem = menu.submenu?.find(
        (item: any) =>
          pathname.includes(item?.submenuPath) ||
          item?.relatedPaths?.some((related: string) => pathname.includes(related))
      );
    } else {
      // Check for direct submenu path matches (for cases where submenuPath is the full path)
      for (const menuItem of features.mainSidebarItems) {
        if (menuItem.submenu) {
          submenuItem = menuItem.submenu.find((item: any) => 
            item.submenuPath !== "/" && pathname === item.submenuPath
          );
          if (submenuItem) {
            menu = menuItem;
            break;
          }
        }
      }
    }

    return {
      menuId: menu ? menu.id : null,
      submenuId: submenuItem ? submenuItem.id : null,
    };
  };

  // To do
  // const navigateToRespectiveStepPage = (productId: number, params: any) => {
  //   const step = getAssessmentStep(vendorDetails.status);
  //   const params: any = { vendorId: vendorDetails.id };
  //   const query = new URLSearchParams(params).toString();

  //   if (step) {
  //     const path = step.path === "" ? "/create-product/shopping-list-management" : step.path;

  //     router.push(`/${path}/${productId}?${query}`);
  //   }
  // };

  return { router, pathname, goToProductPage, getFeatureIdsFromPath };
};

export const navigateToProductForm = (
  router: any,
  ideaId?: string | number,
  vendorId?: string | number,
  redirectToAnotherPage?: string,
  vendorStatus?: string
) => {
  // Check if we need to redirect to another page based on status
  if (redirectToAnotherPage && redirectToAnotherPage?.trim() !== "" && ideaId) {
    // Normalize the status - convert to uppercase to match status-config.json keys
    const normalizedStatus = redirectToAnotherPage?.toUpperCase();
    
    // Check if this status exists in our configuration
    const statusConfigEntry = statusConfig[normalizedStatus as keyof typeof statusConfig];
    
    if (statusConfigEntry) {
      // Build the redirect URL using the configuration
      const redirectUrl = `${statusConfigEntry.redirect_url}/${ideaId}`;
      const redirectParams: any = {};
      
      if (vendorId) {
        redirectParams.vendorId = String(vendorId);
      }
      
      const redirectQuery = new URLSearchParams(redirectParams)?.toString();
      const finalRedirectUrl = redirectQuery ? `${redirectUrl}?${redirectQuery}` : redirectUrl;

      router.push(finalRedirectUrl);
      return; // Exit early, we've handled the redirect
    } else {
      console.log("⚠️ STATUS NOT FOUND IN CONFIG - Using default navigation", {
        redirectToAnotherPage,
        normalizedStatus,
        availableStatuses: Object.keys(statusConfig)
      });
    }
  }
  
  // If vendorId and vendorStatus are provided, route based on vendor status
  if (vendorId && vendorStatus && ideaId) {
    console.log(vendorStatus, "vendorStatus")
    // Normalize the vendor status - handle various formats
    let normalizedVendorStatus = vendorStatus?.toUpperCase().trim();
    
    // Handle common status variations
    const statusMappings: Record<string, string> = {
      "IN PROGRESS": "IN PROGRESS",
      "VENDOR ASSESSMENT": "VENDOR ASSESSMENT",
      "VENDOR APPROVED": "VENDOR APPROVED",
      "AWAITING BUSINESS REVIEW": "AWAITING BUSINESS REVIEW",
      "READY FOR TASTING PANEL": "TASTING PANEL APPROVED",
      "TASTING PANEL APPROVED": "TASTING PANEL APPROVED",
    };
    
    // Check if we have a direct mapping
    const mappedStatus = statusMappings[normalizedVendorStatus];
    if (mappedStatus) {
      normalizedVendorStatus = mappedStatus;
    }
    
    // Replace underscores with spaces if present
    normalizedVendorStatus = normalizedVendorStatus.replace(/_/g, ' ');
    
    // Check if this status exists in our configuration
    const statusConfigEntry = statusConfig[normalizedVendorStatus as keyof typeof statusConfig];
    
    if (statusConfigEntry) {
      // Build the redirect URL using the configuration
      const redirectUrl = `${statusConfigEntry.redirect_url}/${ideaId}`;
      const redirectParams: any = {
        vendorId: String(vendorId)
      };
      
      const redirectQuery = new URLSearchParams(redirectParams)?.toString();
      const finalRedirectUrl = redirectQuery ? `${redirectUrl}?${redirectQuery}` : redirectUrl;

      router.push(finalRedirectUrl);
      return; // Exit early, we've handled the redirect
    } 
  }
  
  // Original implementation - unchanged (fallback for all cases where no redirect happens)
  const params: any = {};
  let routePath = "/create-product/shopping-list-management";

  if (!ideaId) {
    // create product form
    params.create = 1;
  }
  if (ideaId) {
    // edit product form
    // params.ideaId = String(ideaId);
    routePath += `/${ideaId}`;
    if (vendorId) {
      // edit selected vendor form
      params.vendorId = String(vendorId);
    }
  }
  const query = new URLSearchParams(params).toString();
  router.push(routePath + "?" + query);
};



export const useRedirectToActiveTab = () => {
  const { setActiveTab } = useGlobalStateContext();

  const redirectToActiveTab = useCallback(
    (action: string) => {
      const tabIndexMap: Record<string, number> = {
        Hold: 1,
        Cancel: 2,
      };

      setActiveTab(tabIndexMap[action] ?? 0);
    },
    [setActiveTab]
  );

  return redirectToActiveTab;
};
